import Tagline from "./Tagline";
import Carousel from "./Carousel";
import IntroSection from "./IntroSection";
import Challenges from "./Challenges";
import Portfolio from "./Portfolio";
import ProductSection from "./ProductSection";
import Blog from "./Blog";
import Testimonials from "./Testimonials";
import { useTranslation } from "react-i18next";
import HeroStatic from "../../components/hero/HeroStatic";
import { ICallToAction } from "../../components/hero/IHeroProps";
import SEO from "../../app/SEO";
import { makeStyles } from "@fluentui/react-components";
import { useHeroMainSnap } from "../../hooks/useHeroMainSnap";
import { Link } from "react-router-dom";
import { Apps48Regular, Cloud48Regular, Shield48Regular, Sparkle48Regular, Headphones48Regular } from "@fluentui/react-icons";
import KeyFeatures, { IKeyFeaturesCard } from "./KeyFeatures";
import KeyServices, { IKeyServicesCard } from "./KeyServices";


const useStyles = makeStyles({
    root: {
        width: "100%",
        height: "100%"
    },
    heroWrapper: {
        width: "100%",
        height: "auto"
    },
    mainWrapper: {
        position: "relative",
        width: "100%"
    },
    tintBand: {
        position: "relative",
        backgroundColor: "#F4F8F9",
        padding: "4.5rem 0 6rem"
    },
    waveEdge: {
        position: "absolute",
        left: 0,
        top: "-69px",
        width: "100%",
        height: "70px",
        pointerEvents: "none",
        display: "block"
    },
    viewAllContainer: {
        display: "flex",
        justifyContent: "center",
        padding: "2rem 0 3rem"
    },
    viewAllButton: {
        backgroundColor: "#3b9edd",
        color: "white",
        borderRadius: "50px",
        padding: "0.75rem 2rem",
        fontSize: "1rem",
        textDecoration: "none",
        display: "inline-block",
        transition: "all 0.3s",

        ":hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
        }
    }
});

const homeKeyServicesCards: IKeyFeaturesCard[] = [
    { icon: <Apps48Regular />, titleKey: "keyServices.bps.title", descriptionKey: "keyServices.bps.description", linkUrl: "/services/business-process-solutions", linkTextKey: "keyServices.bps.lnkLearnMore" },
    { icon: <Sparkle48Regular />, titleKey: "keyServices.delivery.title", descriptionKey: "keyServices.delivery.description", linkUrl: "/ai/ai-adoption", linkTextKey: "keyServices.delivery.lnkLearnMore", featured: true, badgeKey: "keyServices.featuredBadge" },
    { icon: <Shield48Regular />, titleKey: "keyServices.security.title", descriptionKey: "keyServices.security.description", linkUrl: "/services/security", linkTextKey: "keyServices.security.lnkLearnMore" },
    { icon: <Cloud48Regular />, titleKey: "keyServices.cloud.title", descriptionKey: "keyServices.cloud.description", linkUrl: "/services/digital-transformation-cloud-migration", linkTextKey: "keyServices.cloud.lnkLearnMore" },
    { icon: <Headphones48Regular />, titleKey: "keyServices.support.title", descriptionKey: "keyServices.support.description", linkUrl: "/support/managed-it-support", linkTextKey: "keyServices.support.lnkLearnMore" },
];

const whyChooseUsCards: IKeyServicesCard[] = [
    { titleKey: "whyChooseUs.strategicGuidance.title", descriptionKey: "whyChooseUs.strategicGuidance.description", linkUrl: "/services/fractionalsolutions", linkTextKey: "whyChooseUs.strategicGuidance.lnkLearnMore" },
    { titleKey: "whyChooseUs.solutionDelivery.title", descriptionKey: "whyChooseUs.solutionDelivery.description", linkUrl: "/services/business-process-solutions", linkTextKey: "whyChooseUs.solutionDelivery.lnkLearnMore" },
    { titleKey: "whyChooseUs.ongoingPartnership.title", descriptionKey: "whyChooseUs.ongoingPartnership.description", linkUrl: "/support/managed-it-support", linkTextKey: "whyChooseUs.ongoingPartnership.lnkLearnMore" },
    { titleKey: "whyChooseUs.outsourcedIt.title", descriptionKey: "whyChooseUs.outsourcedIt.description", linkUrl: "/ourstory", linkTextKey: "whyChooseUs.outsourcedIt.lnkLearnMore" },
];

const Home = () => {
    const { t } = useTranslation("home");
    const styles = useStyles();

    const cta: ICallToAction = {
        text: t("hero.callToAction"),
        url: "/services/business-process-solutions"
    };

    const secondaryCta: ICallToAction = process.env.REACT_APP_CLOUD_AUDIT_PATH
        ? {
            text: t("hero.secondaryCallToAction"),
            url: process.env.REACT_APP_CLOUD_AUDIT_PATH,
            tooltip: t("hero.secondaryCallToActionTooltip")
        }
        : null!;

    useHeroMainSnap("home-hero", "home-main", {
        duration: 900,
        easing: "quart",
        heroCapturePx: 80,
        upCapturePx: 300,
        headerOffset: 0
    });

    return (
        <div className={styles.root}>
            <SEO />

            <div id="home-hero" className={styles.heroWrapper}>
                <HeroStatic
                    title={t("hero.title")}
                    subtitle={t("hero.description")}
                    cta={cta}
                    secondaryCta={secondaryCta}
                />
            </div>

            <div id="home-main" className={styles.mainWrapper}>
                <IntroSection />
                <Tagline h2Key="keyServices.h2" h4Key="keyServices.h4" eyebrowKey="keyServices.eyebrow" refined />
                <KeyFeatures cards={homeKeyServicesCards} />
                <Challenges
                    title={t("contactForm.title")}
                    subtitle={t("contactForm.subtitle")}
                    bullets={t("contactForm.bullets", { returnObjects: true }) as string[]}
                />
                <div className={styles.tintBand}>
                    <svg className={styles.waveEdge} viewBox="0 0 1440 90" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                        <path d="M0 38C160 10 330 6 520 26C720 48 930 62 1130 40C1260 26 1370 14 1440 18V90H0Z" fill="#06657E" />
                        <path d="M0 62C200 34 400 34 620 56C820 76 1010 84 1200 64C1310 52 1390 44 1440 46V90H0Z" fill="#F4F8F9" />
                    </svg>
                    <Tagline h2Key="whyChooseUs.h2" h4Key="whyChooseUs.h4" eyebrowKey="whyChooseUs.eyebrow" align="start" refined />
                    <KeyServices cards={whyChooseUsCards} />
                </div>
                <Carousel />
                <div className={styles.viewAllContainer}>
                    <Link to="/services" className={styles.viewAllButton}>
                        {t("keyServices.viewAll")}
                    </Link>
                </div>
                <ProductSection />
                <Portfolio />
                <Blog />
                <Testimonials />
            </div>
        </div>
    );
};

export default Home;